// No-login order history for guests.
//
// A guest who never signs in still needs to find their way back to an order
// they just placed , to watch its status or to pay for it. The order's access
// token (minted server-side in app/api/public/orders/route.ts) is the only
// credential needed to re-open a "track my order" view, so we keep a small,
// device-local list of {order id + token + a little display context} in
// localStorage. Signed-in customers additionally get server-side history
// (recordCustomerOrder); this is the layer that serves everyone else.
//
// Deliberately client-only and best-effort: every access is guarded so a
// browser with storage disabled (private mode, locked-down embed) degrades to
// "no saved orders" rather than throwing into the ordering flow.

import { isUnpaidPaymentStatus } from '@/lib/customer-contract/payment';
import {
  DECLINE_REASON_KEY,
  isAwaitingMerchantApproval,
  readPaymentDeadline,
} from '@/lib/pickup-approval';

/** One line of the order, kept only for the history card's summary. */
export type GuestOrderRecordItem = { title: string; quantity: number };

export type GuestOrderRecord = {
  /** service_orders.id , also the bearer secret for the payment page. */
  id: string;
  /** Order access token , the credential for the status/track view. */
  token: string;
  orderNumber: string;
  /** The short kitchen/counter ticket label shown with the live stage. */
  ticketCode?: string | null;
  branchName?: string | null;
  businessName?: string | null;
  businessId?: string | null;
  branchId?: string | null;
  status?: string | null;
  paymentStatus?: string | null;
  /** Present only on the fresh signed-in server projection, never in device storage. */
  source?: 'account';
  /** Menu slug the order was placed from, so the full-page track link is valid. */
  menuSlug?: string | null;
  total: number;
  currency?: string | null;
  /**
   * What was ordered. Device-local history is the only place this survives
   * without a round-trip, so the card can say "2× Jollof, 1× Water" instead of
   * a bare total. Capped at save time , the card only ever shows the first few.
   */
  items?: GuestOrderRecordItem[];
  /** Service channel the order was placed on: pickup | delivery | dine_in | counter. */
  orderType?: string | null;
  /** epoch ms at save time. */
  createdAt: number;
};

const STORAGE_KEY = 'mnrq:guest-orders:v1';
const MAX_RECORDS = 20;
const TTL_MS = 45 * 24 * 60 * 60 * 1000; // 45 days , long enough to cover a return visit, short enough to self-clean.

function safeParse(raw: string | null): GuestOrderRecord[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (entry): entry is GuestOrderRecord =>
        Boolean(entry) &&
        typeof entry.id === 'string' &&
        typeof entry.token === 'string' &&
        typeof entry.createdAt === 'number',
    );
  } catch {
    return [];
  }
}

/** Read the stored orders, newest first, pruning anything past its TTL. */
export function listGuestOrders(): GuestOrderRecord[] {
  if (typeof window === 'undefined') return [];
  let records: GuestOrderRecord[];
  try {
    records = safeParse(window.localStorage.getItem(STORAGE_KEY));
  } catch {
    return [];
  }

  const cutoff = Date.now() - TTL_MS;
  const fresh = records.filter((r) => r.createdAt >= cutoff);
  fresh.sort((a, b) => b.createdAt - a.createdAt);

  // Write back only when pruning actually removed something, to avoid a
  // storage write on every read.
  if (fresh.length !== records.length) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(fresh));
    } catch {
      /* best-effort */
    }
  }
  return fresh;
}

/** Save (or refresh) one order. De-dupes by id and keeps the newest MAX_RECORDS. */
export function saveGuestOrder(record: GuestOrderRecord): void {
  if (typeof window === 'undefined') return;
  if (!record.id || !record.token) return;
  try {
    const existing = safeParse(window.localStorage.getItem(STORAGE_KEY)).filter(
      (r) => r.id !== record.id,
    );
    const next = [{ ...record, createdAt: record.createdAt || Date.now() }, ...existing].slice(
      0,
      MAX_RECORDS,
    );
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    // Let any mounted launcher in this tab refresh immediately (the native
    // `storage` event only fires in *other* tabs).
    window.dispatchEvent(new Event('mnrq:guest-orders-changed'));
  } catch {
    /* best-effort */
  }
}

export function removeGuestOrder(id: string): void {
  if (typeof window === 'undefined') return;
  try {
    const next = safeParse(window.localStorage.getItem(STORAGE_KEY)).filter((r) => r.id !== id);
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    window.dispatchEvent(new Event('mnrq:guest-orders-changed'));
  } catch {
    /* best-effort */
  }
}

export function clearGuestOrders(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
    window.dispatchEvent(new Event('mnrq:guest-orders-changed'));
  } catch {
    /* best-effort */
  }
}

/** Whether a payment status still owes money (drives the "Pay now" affordance). Shared with the app. */
export { isUnpaidPaymentStatus };

/**
 * Whether the COUNTER's verdict — not the kitchen — is what this guest is
 * waiting on. docs/operational-ux-2026-09/07-customer-ordering.md §5.
 *
 * Lives here, beside the other "how does a guest read their own order"
 * helpers, because TWO surfaces have to agree: the tracker sheet on the menu
 * and the standalone order page a shared link opens. They had drifted the
 * moment only one of them was taught the flow — the page went on announcing
 * "Sent — waiting for the kitchen", with a four-stage rail and an offer to
 * cancel "now the kitchen has started", for an order the kitchen had not been
 * told about and nobody had agreed to make.
 *
 * Goes quiet once the order is paid and cooking: from there the rail is right
 * again, and this must not hold a screen it no longer describes.
 */
export function pickupApprovalGovernsStatus(order: {
  status: string;
  paymentStatus: string;
  pickupApproval?: Record<string, unknown> | null;
}): boolean {
  const approval = order.pickupApproval;
  if (!approval) return false;
  if (isAwaitingMerchantApproval(approval)) return true;
  if (order.status === 'expired') return true;
  if (order.status === 'cancelled') return Boolean(approval[DECLINE_REASON_KEY]);
  // Accepted and unpaid: the payment window is running, and the deadline is
  // what proves this flow armed it rather than an ordinary prepay order.
  return (
    order.status === 'accepted' &&
    isUnpaidPaymentStatus(order.paymentStatus) &&
    readPaymentDeadline(approval) !== null
  );
}

// ── Cross-tab sync ─────────────────────────────────────────────────────────
// The native `storage` event fires in other tabs when localStorage changes.
// We listen for it and re-dispatch our custom event so React hooks in other
// tabs also refresh their data.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key === STORAGE_KEY) {
      window.dispatchEvent(new Event('mnrq:guest-orders-changed'));
    }
  });
}
